import { ref, onMounted, computed } from 'vue';
import type { RouteStop } from '@/domains/routeStop/types/RouteStop';
import type { ApiResponse } from '@/types/api';
import {
  observeAllRouteStops,
  getAllRouteStopsFromCache,
  upsertRouteStops,
} from '@/domains/routeStop/repositories/routeStopRepository';
import { useAutoUnsubscribe } from '@/composables/useAutoUnsubscribe';
import { useApiQuery } from '@/composables/useApi';
import { routeStopsEndpoints } from '@/services/api/apiConfig';

const routeStops = ref<RouteStop[]>([]);
const error = ref<Error | null>(null);

export const useRouteStops = () => {
  // Dependencies
  const { trackSubscription } = useAutoUnsubscribe();

  const routeStopsQuery = useApiQuery<ApiResponse<RouteStop[]>, Error>({
    endpoint: routeStopsEndpoints.LIST(),
    enabled: false, // Only fetch when explicitly called via refetch
    queryKey: ['routeStops'],
  });

  // Infrastructure helpers
  const fetchRouteStopsFromApi = async (): Promise<RouteStop[]> => {
    const result = await routeStopsQuery.refetch();
    const response = result.data;
    if (!response?.data) {
      throw new Error('No route stop data available in API response');
    }
    return response.data;
  };

  // Business Actions
  const loadRouteStops = async (): Promise<void> => {
    if (routeStopsQuery.isLoading.value) {
      return;
    }

    error.value = null;

    try {
      const routeStopsData = await fetchRouteStopsFromApi();
      const updatedRouteStops = await upsertRouteStops(routeStopsData);
      routeStops.value = updatedRouteStops;
    } catch (e) {
      // API failed - fallback to cache
      console.warn('[useRouteStops] API sync failed, falling back to cache:', e);

      const cachedRoutes = await getAllRouteStopsFromCache();
      if (cachedRoutes && cachedRoutes.length > 0) {
        // Cache has data - use it
        routeStops.value = cachedRoutes;
        error.value = null; // Don't set error if we have cache to fallback to
        return;
      }
      // No cache either - this is a real error
      error.value = e instanceof Error ? e : new Error('Failed to fetch broker routes');
      throw e;
    }
  };

  // Business Selectors
  const getRouteStopById = (routeStopId: number): RouteStop | null => {
    return (routeStops.value ?? []).find((s) => s.id === routeStopId) ?? null;
  };
  const getRouteStopsByRouteId = (routeId: number): RouteStop[] | null => {
    return (routeStops.value ?? []).filter((s) => s.routeId === routeId) ?? null;
  };

  // === LIFECYCLE ===

  onMounted(() => {
    trackSubscription(
      observeAllRouteStops().subscribe({
        next: (routeStopRows: RouteStop[]) => {
          routeStops.value = routeStopRows ?? [];
        },
        error: () => {
          // no-op: keep previous state
        },
      })
    );
  });

  // === COMPOSABLE OUTPUT ===

  const state = {
    routeStops, // all route stops related to a user in memory
  };

  const selectors = {
    getRouteStopById,
    getRouteStopsByRouteId,
  };

  const actions = {
    loadRouteStops,
  };

  const status = {
    isLoading: computed(() => routeStopsQuery.isLoading.value),
    error: computed(() => error.value || routeStopsQuery.error.value),
  };

  return {
    state,
    selectors,
    actions,
    status,
  };
};
